import { useQueryClient } from '@tanstack/react-query';
import { FormComponentValue } from 'components/common/form/layout/control';
import { pageAPI } from 'api/editor/page';
import { TagSource } from './index';
import { YNValues } from 'tools/types';
import { useCallback } from 'react';

interface LoadOptionsParams {
    portalPageId: string;
    loadSelectDataUrl: string;
    controlPropsValue?: any;
}

export const SELECT_LIMIT = 1000;

export function usePageVariablesOptions({ portalPageId, loadSelectDataUrl, controlPropsValue }: LoadOptionsParams) {
    const queryClient = useQueryClient();

    const loadOptions = useCallback(
        async (
            inputValue: string,
            additionalOptions: string,
            source: TagSource,
            value: string,
            multiple: YNValues,
            init?: boolean,
            index: number = 0,
        ): Promise<FormComponentValue[]> => {
            const tempDatasetId = (() => {
                try {
                    const parsed = JSON.parse(controlPropsValue?.[0]?.value || '{}');
                    return Array.isArray(parsed) ? parsed?.[index || 0]?.['Dataset'] : parsed?.['Dataset'];
                } catch {
                    return controlPropsValue?.[0]?.value;
                }
            })();
            let datasetId = 0;
            if (value && source === 'dataset') {
                datasetId = +value;
            } else if (tempDatasetId) {
                datasetId = tempDatasetId;
            }
            const initKey = init ? value : '';

            const limitParam = source !== 'static' ? `&limit=${SELECT_LIMIT}` : '';
            const queries = `?source=${source}&format=list${limitParam}`;
            const queryKey = [
                'page-variables-options',
                source,
                inputValue,
                portalPageId,
                source === 'dataset_column' ? datasetId : null,
                additionalOptions,
                initKey,
            ];

            const cachedData = queryClient.getQueryData<FormComponentValue[]>(queryKey);
            if (cachedData) {
                return cachedData;
            }

            const fetchData = async () => {
                try {
                    const response = await pageAPI.loadSelectData(
                        {
                            portalPageId,
                            additionalOptions,
                            source,
                            search: inputValue,
                            key: init ? value : '',
                            multiple,
                        },
                        `${loadSelectDataUrl}${queries}${source === 'dataset_column' ? `&datasetId=${datasetId}` : ''}`,
                    );

                    if (!response?.results) return [];

                    return response.results.map((item: any) =>
                        typeof item === 'string' ? { value: item, label: item } : { value: item.id, label: item.text },
                    );
                } catch (err) {
                    console.error('Failed to load options', err);
                    return [];
                }
            };

            const data = await queryClient.fetchQuery({
                queryKey,
                queryFn: fetchData,
                staleTime: 5 * 60 * 1000,
            });

            return data;
        },
        [portalPageId, loadSelectDataUrl, queryClient, controlPropsValue],
    );

    return { loadOptions };
}
